import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, PlayCircle, Loader2 } from "lucide-react";
import { usePublicTenantTheme } from "@/lib/usePublicTenantTheme";

export default function Login() {
  const { login, setSession } = useAuth();
  const navigate = useNavigate();
  const branding = usePublicTenantTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back");
      const next = sessionStorage.getItem("ll_after_login");
      sessionStorage.removeItem("ll_after_login");
      const isClient = data?.user?.role === "client";
      navigate(isClient ? (next?.startsWith("/client") ? next : "/client") : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const { data } = await api.post("/auth/demo");
      setSession(data.token, data.user, data.tenant);
      toast.success("Demo workspace ready — explore freely!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start the demo. Try again.");
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--ll-bg)" }}>
      <div
        className="hidden lg:flex flex-col justify-between p-12 border-r relative ll-grain"
        style={{
          borderColor: "var(--ll-border)",
          background: branding?.brand_color || "#0b5d9e",
          color: "white",
        }}
        data-testid="login-hero"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          {branding?.logo_base64 ? (
            <img src={branding.logo_base64} alt={branding.name || "logo"} className="h-9 w-auto" style={{ maxWidth: 160 }} />
          ) : (
            <div className="w-7 h-7 grid place-items-center bg-white text-[var(--ll-ink)]"><span className="font-display font-bold text-sm">F</span></div>
          )}
          {!branding?.logo_base64 && (
            <span className="font-display font-bold text-lg">{branding?.name || "Financials Ready"}</span>
          )}
        </Link>
        <div>
          {branding?.logo_base64 && (
            <img
              src={branding.logo_base64}
              alt={branding.name || "logo"}
              className="mb-6 h-16 w-auto"
              style={{ maxWidth: 260 }}
              data-testid="login-hero-logo"
            />
          )}
          <div className="font-display font-bold text-4xl xl:text-5xl leading-tight" data-testid="login-hero-title">
            {branding?.name ? `Sign in to ${branding.name}.` : "Sign in to your books."}
          </div>
          <p className="mt-4 text-white/70 max-w-md">
            {branding?.name
              ? `Your workspace at ${branding.name} — reconciled positions, AI-tagged transactions, ready-to-export reports.`
              : "Your reconciled positions, AI-tagged transactions, and ready-to-export reports — exactly where you left them."}
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white">Made for finance</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">Sign in</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-8">Welcome back.</h1>
          <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full px-3 py-2.5 border bg-white mb-4" style={{ borderColor: "var(--ll-border)" }}
            placeholder="you@company.com"
          />
          <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Password</label>
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="w-full px-3 py-2.5 border bg-white mb-6" style={{ borderColor: "var(--ll-border)" }}
            placeholder="••••••••"
          />
          <button
            type="submit" disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-[var(--ll-ink)] text-white py-3 hover:bg-[#0b5d9e] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Signing in…" : <>Sign in <ArrowRight size={15} /></>}
          </button>
          <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            <div className="flex-1 h-px bg-[var(--ll-border)]" />
            <span>or</span>
            <div className="flex-1 h-px bg-[var(--ll-border)]" />
          </div>
          <button
            type="button"
            onClick={startDemo}
            disabled={demoLoading}
            data-testid="login-try-demo-btn"
            className="w-full border border-[var(--ll-ink)] text-[var(--ll-ink)] py-3 hover:bg-[var(--ll-mint)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {demoLoading ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
            {demoLoading ? "Spinning up a demo…" : "Try the live demo"}
          </button>
          <div className="mt-4 text-sm text-[var(--ll-ink-soft)]">
            <Link to="/forgot-password" className="hover:text-[#0b5d9e] hover:underline underline-offset-4" data-testid="link-forgot-password">
              Forgot password?
            </Link>
            <span className="mx-2 opacity-40">·</span>
            <Link to="/forgot-password?mode=username" className="hover:text-[#0b5d9e] hover:underline underline-offset-4" data-testid="link-forgot-username">
              Forgot username?
            </Link>
          </div>
          <div className="mt-4 text-sm text-[var(--ll-ink-soft)]">
            Don&apos;t have a workspace? <Link to="/signup" className="text-[var(--ll-ink)] underline underline-offset-4" data-testid="link-to-signup">Create one</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
